import { useState, useEffect, useCallback } from 'react'
import { useConfirm } from '@/components/common/ConfirmDialog'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
  Plus, Trash2, ChevronUp, ChevronDown, UserCheck, UploadCloud, RefreshCw,
  ShieldCheck, GitCompare, ExternalLink, CheckCircle2, AlertTriangle, XCircle,
  Users, Clock, SlidersHorizontal, ArrowRightLeft, Save,
} from 'lucide-react'
import type {
  TransactionModel, ApprovalConfig, ApprovalLevel, ApprovalApprover,
  ApprovalOverrideDecl, ApprovalActionBinding, ActionDef,
} from './types'
import { legacyActionToDef } from './types'
import {
  approvalsVersions, approvalsPublish, approvalsValidate, approvalsDiff,
  approvalsMigrate, approvalOverrideList, approvalOverrideSet, approvalOverrideDelete,
  type ApprovalVersionMeta, type ApprovalValidationIssue, type ApprovalDiff,
  type ApprovalOverride,
} from '@/livepreview/api'
import {
  listFunctionalRoles, listFunctionalRoleUsers, type FunctionalRoleUser,
} from '@/api/team'
import { parseApiError } from '@/livepreview/utils'

interface ApprovalsEditorProps {
  model: TransactionModel
  onModelChange: (model: TransactionModel) => void
  // Project folder name + obj_name — needed to call the publish / versions APIs.
  project: string
  objName: string
  // Wraps navigation so unsaved transaction edits prompt to save before we
  // jump to the Workflow Designer. Falls back to navigating directly.
  onGuardedNavigate?: (action: () => void) => void
}

// ── Styles (mirrors the constants used by the neighbouring editors in
// PropertyPanel.tsx so the surface looks consistent) ──────────────────────
const sectionStyle: React.CSSProperties = { marginBottom: 16 }

const sectionTitleStyle: React.CSSProperties = {
  fontSize: '0.8625rem', fontWeight: 600, color: 'var(--color-accent)',
  marginBottom: 8, paddingBottom: 4, borderBottom: '1px solid var(--color-border)',
}

const fieldLabelStyle: React.CSSProperties = {
  display: 'block', fontSize: '0.8125rem', color: 'var(--color-text-secondary)',
  fontWeight: 600,
  marginBottom: 2, marginTop: 8,
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '7px 10px', background: 'var(--color-input-bg)',
  border: '1px solid var(--color-border-strong)', borderRadius: 6,
  color: 'var(--color-text)', fontSize: '0.8625rem', minHeight: 36, boxSizing: 'border-box',
}

const checkboxRowStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6, marginTop: 6,
  fontSize: '0.8625rem', color: 'var(--color-text)',
}

const addBtnStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
  padding: '6px 8px', fontSize: '0.8125rem', color: 'var(--color-accent)',
  background: 'none', border: '1px dashed var(--color-border)', borderRadius: 4,
  cursor: 'pointer', width: '100%', marginTop: 4,
}

const helpTextStyle: React.CSSProperties = {
  fontSize: '0.8125rem', color: 'var(--color-text-secondary)',
  margin: '0 0 12px', lineHeight: 1.5,
}

const iconBtnStyle: React.CSSProperties = {
  background: 'none', border: 'none', cursor: 'pointer',
  color: 'var(--color-text-secondary)', padding: 2,
  display: 'flex', alignItems: 'center', flexShrink: 0,
}

// Default config seeded the first time approval is switched on for an object.
function defaultApprovalConfig(objName: string): ApprovalConfig {
  return {
    enabled: true,
    workflow: `approval_${(objName || '').toLowerCase()}`,
    profile: 'linear',
    triggers: ['add', 'edit'],
    single_active_per_record: true,
    levels: [],
  }
}

const STATUS_COLORS: Record<string, string> = {
  active: '#26a69a', superseded: 'var(--color-text-secondary)', archived: '#b26a00',
}

export default function ApprovalsEditor({
  model, onModelChange, project, objName, onGuardedNavigate,
}: ApprovalsEditorProps) {
  const navigate = useNavigate()
  const approval = model.approval
  // 'linear' = the rule-based levels authored here; 'advanced' = a graphical
  // (XPDL) workflow authored in the Workflow Designer.
  const profile: 'linear' | 'advanced' =
    approval?.profile === 'advanced' ? 'advanced' : 'linear'

  // ── Versions state (published immutable workflow versions) ──────────────
  const [versions, setVersions] = useState<ApprovalVersionMeta[]>([])
  const { confirm, confirmDialog } = useConfirm()
  const [activeVersion, setActiveVersion] = useState<number | null>(null)
  const [loadingVersions, setLoadingVersions] = useState(false)
  const [publishing, setPublishing] = useState(false)
  const [publishNotes, setPublishNotes] = useState('')

  // ── Validation against the engine's executable subset ────────────────────
  const [validating, setValidating] = useState(false)
  const [issues, setIssues] = useState<ApprovalValidationIssue[] | null>(null)

  // ── Version comparison (semantic diff between two published versions) ─────
  const [diffFrom, setDiffFrom] = useState<number | null>(null)
  const [diffTo, setDiffTo] = useState<number | null>(null)
  const [diff, setDiff] = useState<ApprovalDiff | null>(null)
  const [diffing, setDiffing] = useState(false)

  // ── In-flight migration (move pending approvals onto a newer version) ─────
  const [migrating, setMigrating] = useState(false)

  // ── Per-tenant override VALUES (the stored knob values for this project) ──
  const [overrideValues, setOverrideValues] = useState<ApprovalOverride[]>([])
  const [overridesLoaded, setOverridesLoaded] = useState(false)
  const [loadingOverrides, setLoadingOverrides] = useState(false)
  // Working copy of each value while it's being edited, keyed by override name.
  const [overrideEdits, setOverrideEdits] = useState<Record<string, string>>({})
  const [savingOverride, setSavingOverride] = useState<string | null>(null)

  const loadVersions = useCallback(async () => {
    if (!project || !objName) return
    setLoadingVersions(true)
    try {
      const res = await approvalsVersions(project, objName)
      setVersions(res.versions || [])
      setActiveVersion(res.active_version ?? null)
    } catch (err) {
      // A workflow that has never been published 404s / returns empty — treat
      // that as "no versions yet" rather than an error the user must act on.
      const parsed = parseApiError(err)
      if (parsed.status && parsed.status !== 404) {
        toast.error(`Could not load approval versions: ${parsed.message}`)
      }
      setVersions([])
      setActiveVersion(null)
    } finally {
      setLoadingVersions(false)
    }
  }, [project, objName])

  // Fetch the published versions on mount (and whenever the object changes).
  useEffect(() => { void loadVersions() }, [loadVersions])

  // Seed the compare dropdowns with (previous → newest) once ≥2 versions exist.
  useEffect(() => {
    if (versions.length >= 2) {
      setDiffTo((prev) => prev ?? versions[0].version)
      setDiffFrom((prev) => prev ?? versions[1].version)
    }
  }, [versions])

  // ── Functional roles + project users (for the approver lookups) ─────────
  // Loaded once so the "Approver role" combobox can suggest existing roles and
  // the "specific users" picker can offer project members. Both are best-effort
  // — on any failure we fall back to plain free-text entry.
  const [roleOptions, setRoleOptions] = useState<string[]>([])
  const [userOptions, setUserOptions] = useState<FunctionalRoleUser[]>([])

  useEffect(() => {
    if (!project) return
    let cancelled = false
    void (async () => {
      try {
        const [rolesRes, usersRes] = await Promise.all([
          listFunctionalRoles(project),
          listFunctionalRoleUsers(project),
        ])
        if (cancelled) return
        setRoleOptions(Array.isArray(rolesRes?.roles) ? rolesRes.roles : [])
        setUserOptions(Array.isArray(usersRes?.users) ? usersRes.users : [])
      } catch {
        // Non-fatal: the approver inputs degrade to free text.
        if (!cancelled) {
          setRoleOptions([])
          setUserOptions([])
        }
      }
    })()
    return () => { cancelled = true }
  }, [project])

  // Deep-clone the model, mutate its approval config, and push the change up.
  const patchApproval = (patch: Partial<ApprovalConfig>) => {
    const m: TransactionModel = JSON.parse(JSON.stringify(model))
    const base = m.approval || defaultApprovalConfig(objName)
    m.approval = { ...base, ...patch }
    onModelChange(m)
  }

  const setLevels = (levels: ApprovalLevel[]) => patchApproval({ levels })

  // Toggle the master switch. Turning it on for the first time seeds defaults;
  // turning it off keeps the configured levels but stops routing saves through
  // approval.
  const toggleEnabled = (enabled: boolean) => {
    if (enabled && !approval) {
      const m: TransactionModel = JSON.parse(JSON.stringify(model))
      m.approval = defaultApprovalConfig(objName)
      onModelChange(m)
      return
    }
    patchApproval({ enabled })
  }

  // One trigger checkbox (add / edit) bound to approval.triggers.
  const toggleTrigger = (trigger: 'add' | 'edit', on: boolean) => {
    const cur = approval?.triggers || []
    const next = on
      ? Array.from(new Set([...cur, trigger]))
      : cur.filter((t) => t !== trigger)
    patchApproval({ triggers: next })
  }

  const levels = approval?.levels || []

  // ── Per-tenant override DECLARATIONS (approval.overrides[]) ──────────────
  const overrides = approval?.overrides || []
  const setOverrides = (next: ApprovalOverrideDecl[]) =>
    patchApproval({ overrides: next })
  const patchOverride = (idx: number, patch: Partial<ApprovalOverrideDecl>) =>
    setOverrides(overrides.map((o, i) => (i === idx ? { ...o, ...patch } : o)))
  const addOverride = () => setOverrides([...overrides, { name: '' }])
  const removeOverride = (idx: number) =>
    setOverrides(overrides.filter((_, i) => i !== idx))

  // ── Publish ─────────────────────────────────────────────────────────────
  const handlePublish = async () => {
    if (!project || !objName) {
      toast.error('Project / object not resolved — cannot publish.')
      return
    }
    if (!approval?.enabled) {
      toast.error('Enable approval before publishing a version.')
      return
    }
    if (!levels.some((l) => (l.name || '').trim())) {
      toast.error('Add at least one approval level before publishing.')
      return
    }
    setPublishing(true)
    try {
      const res = await approvalsPublish(project, objName, {
        notes: publishNotes.trim() || undefined,
      })
      toast.success(
        res.created
          ? `Published ${res.workflow} v${res.version}`
          : `No changes — already on ${res.workflow} v${res.version}`,
      )
      setPublishNotes('')
      await loadVersions()
    } catch (err) {
      const parsed = parseApiError(err)
      toast.error(`Publish failed: ${parsed.message}`)
    } finally {
      setPublishing(false)
    }
  }

  const setProfile = (p: 'linear' | 'advanced') => patchApproval({ profile: p })

  // ── Validate (linear: the workflow rebuilt from the levels) ──────────────
  const handleValidate = async () => {
    if (!project || !objName) return
    setValidating(true)
    setIssues(null)
    try {
      const res = await approvalsValidate(project, objName)
      setIssues(res.issues || [])
      const s = res.summary
      if (s.ok && s.warnings === 0) toast.success('Workflow is valid.')
      else if (s.ok) toast(`Valid — ${s.warnings} warning(s).`, { icon: '⚠️' })
      else toast.error(`${s.errors} blocking issue(s) found.`)
    } catch (err) {
      toast.error(`Validation failed: ${parseApiError(err).message}`)
    } finally {
      setValidating(false)
    }
  }

  // ── Compare two published versions (semantic diff) ───────────────────────
  const handleDiff = async () => {
    if (!project || !objName || diffFrom == null || diffTo == null) return
    if (diffFrom === diffTo) { toast('Pick two different versions.'); return }
    setDiffing(true)
    setDiff(null)
    try {
      const res = await approvalsDiff(project, objName, diffFrom, diffTo)
      setDiff(res.diff)
    } catch (err) {
      toast.error(`Compare failed: ${parseApiError(err).message}`)
    } finally {
      setDiffing(false)
    }
  }

  // ── Migrate in-flight approvals from one published version onto another ───
  // Two-pass: a dry run first to show how many move / are skipped, then (on
  // confirm) the real run. Reuses the compare from/to selects.
  const handleMigrate = async () => {
    if (!project || !objName || diffFrom == null || diffTo == null) return
    if (diffFrom === diffTo) { toast('Pick two different versions.'); return }
    setMigrating(true)
    try {
      const dry = await approvalsMigrate(project, objName, diffFrom, diffTo, { dryRun: true })
      if (dry.migrated === 0 && dry.skipped.length === 0) {
        toast(`No in-flight approvals to migrate from v${diffFrom}.`)
        return
      }
      const skippedNote = dry.skipped.length
        ? `\n\n${dry.skipped.length} would be skipped:\n` +
          dry.skipped.slice(0, 8).map((s) => `• ${s.id}: ${s.reason}`).join('\n') +
          (dry.skipped.length > 8 ? `\n…and ${dry.skipped.length - 8} more` : '')
        : ''
      const ok = await confirm({
        title: 'Migrate in-flight approvals?',
        message: <span style={{ whiteSpace: 'pre-line' }}>{`Migrate ${dry.migrated} in-flight approval(s) from v${diffFrom} → v${diffTo}?${skippedNote}`}</span>,
        confirmLabel: 'Migrate', tone: 'primary',
      })
      if (!ok) return
      const res = await approvalsMigrate(project, objName, diffFrom, diffTo, { dryRun: false })
      toast.success(
        `Migrated ${res.migrated} approval(s) to v${diffTo}` +
        (res.skipped.length ? ` · ${res.skipped.length} skipped` : ''),
      )
      await loadVersions()
    } catch (err) {
      toast.error(`Migration failed: ${parseApiError(err).message}`)
    } finally {
      setMigrating(false)
    }
  }

  // ── Load / save / delete the per-tenant override VALUES ──────────────────
  const loadOverrideValues = async () => {
    if (!project) return
    setLoadingOverrides(true)
    try {
      const res = await approvalOverrideList(project)
      const items = res.overrides || []
      setOverrideValues(items)
      setOverrideEdits(Object.fromEntries(items.map((o) => [o.name, o.value])))
      setOverridesLoaded(true)
    } catch (err) {
      toast.error(`Could not load tenant values: ${parseApiError(err).message}`)
    } finally {
      setLoadingOverrides(false)
    }
  }

  const saveOverrideValue = async (name: string, label?: string) => {
    if (!project || !name) return
    setSavingOverride(name)
    try {
      const value = overrideEdits[name] ?? ''
      await approvalOverrideSet(project, name, value, label ? { label } : {})
      toast.success(`Saved ${name}`)
      await loadOverrideValues()
    } catch (err) {
      toast.error(`Save failed: ${parseApiError(err).message}`)
    } finally {
      setSavingOverride(null)
    }
  }

  const deleteOverrideValue = async (name: string) => {
    if (!project || !name) return
    setSavingOverride(name)
    try {
      await approvalOverrideDelete(project, name)
      toast.success(`Removed ${name}`)
      await loadOverrideValues()
    } catch (err) {
      toast.error(`Delete failed: ${parseApiError(err).message}`)
    } finally {
      setSavingOverride(null)
    }
  }

  // Open a chain in the graphical Workflow Designer. With no `action` this is
  // the object's CRUD chain; with one it is that action button's own chain
  // (its workflow name + an `action:<name>` trigger + the &action= marker the
  // designer publishes back with).
  const openInWorkflowDesigner = (action?: ApprovalActionBinding) => {
    const wf = (action
      ? (action.workflow || defaultActionWorkflow(objName, action.action))
      : (approval?.workflow || `approval_${(objName || '').toLowerCase()}`)).trim()
    // Carry the bound transaction (+ triggers) so the Workflow Designer
    // auto-links to it instead of asking the user to bind again.
    const trig = action
      ? `action:${(action.action || '').toLowerCase()}`
      : (approval?.triggers && approval.triggers.length ? approval.triggers : ['add', 'edit']).join(',')
    const go = () => navigate(
      `/designers/workflow/${encodeURIComponent(project)}/${encodeURIComponent(wf)}`
      + `?bind=${encodeURIComponent(objName)}&triggers=${encodeURIComponent(trig)}`
      + (action ? `&action=${encodeURIComponent(action.action)}` : ''),
    )
    // Prompt to save/discard unsaved transaction edits before leaving.
    if (onGuardedNavigate) onGuardedNavigate(go)
    else go()
  }

  const td: React.CSSProperties = {
    padding: '4px 8px', fontSize: '0.8125rem', color: 'var(--color-text)',
    borderBottom: '1px solid var(--color-border)', textAlign: 'left',
  }
  const th: React.CSSProperties = {
    ...td, fontWeight: 600, color: 'var(--color-text-secondary)',
    borderBottom: '1px solid var(--color-border)',
  }

  return (
    <div style={{ padding: '8px 12px', overflow: 'auto', height: '100%' }}>
      {confirmDialog}
      <div style={{ fontSize: '0.9125rem', fontWeight: 600, marginBottom: 12, color: 'var(--color-text)', display: 'flex', alignItems: 'center', gap: 6 }}>
        <UserCheck size={15} style={{ color: '#5c6bc0' }} /> Approvals
      </div>
      <p style={helpTextStyle}>
        Require one or more approval levels before a record is committed.
        When approval is on, a save creates a pending approval instead of
        writing the record; once every applicable level approves, the record
        is promoted to the live table.
      </p>

      {/* Master switch */}
      <div style={sectionStyle}>
        <label style={{ ...checkboxRowStyle, marginTop: 0, fontWeight: 600 }}>
          <input
            type="checkbox"
            checked={!!approval?.enabled}
            onChange={(e) => toggleEnabled(e.target.checked)}
          />
          <span>Require approval before this record is saved</span>
        </label>
      </div>

      {approval?.enabled && (
        <>
          {/* Workflow style: simple levels vs advanced graphical */}
          <div style={sectionStyle}>
            <div style={sectionTitleStyle}>Workflow style</div>
            <div style={{ display: 'flex', gap: 8 }}>
              {([
                ['linear', 'Simple', 'Rule-based levels, set up right here'],
                ['advanced', 'Advanced', 'A graphical workflow (parallel paths, system steps)'],
              ] as const).map(([key, title, sub]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setProfile(key)}
                  style={{
                    flex: 1, textAlign: 'left', padding: '8px 10px', borderRadius: 6,
                    cursor: 'pointer', fontSize: '0.8125rem', color: 'var(--color-text)',
                    border: `1px solid ${profile === key ? 'var(--color-accent)' : 'var(--color-border)'}`,
                    background: profile === key
                      ? 'var(--color-accent-soft, rgba(92,107,192,0.12))' : 'var(--color-bg)',
                  }}
                >
                  <div style={{ fontWeight: 700 }}>{title}</div>
                  <div style={{ color: 'var(--color-text-secondary)', marginTop: 2 }}>{sub}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Advanced (graphical) banner + deep link */}
          {profile === 'advanced' && (
            <div style={{
              ...sectionStyle, border: '1px solid var(--color-border)', borderRadius: 8,
              padding: 10, background: 'var(--color-bg-secondary)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8425rem', fontWeight: 600, color: 'var(--color-text)' }}>
                <ShieldCheck size={14} style={{ color: '#5c6bc0' }} /> Advanced (graphical) workflow
              </div>
              <p style={{ ...helpTextStyle, margin: '6px 0' }}>
                This object's approval is drawn in the Workflow Designer — parallel
                paths, conditional routing and system steps (email / SQL / call
                action). Author and <strong>publish</strong> the workflow there;
                the published version history stays visible below.
              </p>
              <button
                className="btn btn-sm"
                style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                onClick={() => openInWorkflowDesigner()}
              >
                <ExternalLink size={13} /> Open in Workflow Designer
              </button>
            </div>
          )}

          {/* Triggers */}
          <div style={sectionStyle}>
            <div style={sectionTitleStyle}>When approval applies</div>
            <div style={{ ...fieldLabelStyle, marginTop: 0, fontStyle: 'italic' }}>
              Route these save operations through approval.
            </div>
            <label style={checkboxRowStyle}>
              <input
                type="checkbox"
                checked={(approval.triggers || []).includes('add')}
                onChange={(e) => toggleTrigger('add', e.target.checked)}
              />
              <span>On add (new records)</span>
            </label>
            <label style={checkboxRowStyle}>
              <input
                type="checkbox"
                checked={(approval.triggers || []).includes('edit')}
                onChange={(e) => toggleTrigger('edit', e.target.checked)}
              />
              <span>On edit (changes to existing records)</span>
            </label>

            <label style={{ ...checkboxRowStyle, marginTop: 12 }}>
              <input
                type="checkbox"
                checked={!!approval.single_active_per_record}
                onChange={(e) => patchApproval({ single_active_per_record: e.target.checked })}
              />
              <span>One approval at a time per record</span>
            </label>
            <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: 2, marginLeft: 24 }}>
              Stops two people sending changes to the same record for approval at the same time.
            </div>

            <label style={{ ...checkboxRowStyle, marginTop: 12 }}>
              <input
                type="checkbox"
                checked={!!approval.ioflow_events}
                onChange={(e) => patchApproval({ ioflow_events: e.target.checked })}
              />
              <span>Send approval events to IOFlow (notifications, reminders, SLA escalation)</span>
            </label>
            <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: 2, marginLeft: 24 }}>
              Emits approval.* lifecycle events so IOFlow flows can notify approvers, send reminders and escalate overdue steps.
            </div>

            <label style={fieldLabelStyle}>Workflow name</label>
            <input
              style={inputStyle}
              value={approval.workflow ?? ''}
              placeholder={`approval_${(objName || '').toLowerCase()}`}
              onChange={(e) => patchApproval({ workflow: e.target.value })}
            />
            <div style={{ ...fieldLabelStyle, marginTop: 4, fontStyle: 'italic' }}>
              The stable name the published versions are grouped under.
            </div>
          </div>

          {/* Levels (simple profile only) */}
          {profile === 'linear' && (
            <LevelsSection
              levels={levels}
              onChange={setLevels}
              roleOptions={roleOptions}
              userOptions={userOptions}
              idPrefix="crud"
            />
          )}

          {/* Per-action-button approval chains */}
          <ActionApprovalsSection
            model={model}
            bindings={approval.actions || []}
            onChange={(actions) => patchApproval({ actions })}
            roleOptions={roleOptions}
            userOptions={userOptions}
            project={project}
            objName={objName}
            onOpenWorkflowDesigner={openInWorkflowDesigner}
          />

          {/* Tenant thresholds / overrides (simple profile) */}
          {profile === 'linear' && (
            <OverridesSection
              overrides={overrides}
              onPatchOverride={patchOverride}
              onAddOverride={addOverride}
              onRemoveOverride={removeOverride}
              values={overrideValues}
              valuesLoaded={overridesLoaded}
              loadingValues={loadingOverrides}
              edits={overrideEdits}
              setEdits={setOverrideEdits}
              savingName={savingOverride}
              onLoadValues={() => void loadOverrideValues()}
              onSaveValue={(name, label) => void saveOverrideValue(name, label)}
              onDeleteValue={(name) => void deleteOverrideValue(name)}
            />
          )}

          {/* Validate + Publish (simple profile authors here; advanced publishes
              from the Workflow Designer) */}
          {profile === 'linear' && (
            <div style={sectionStyle}>
              <div style={sectionTitleStyle}>Check &amp; publish</div>
              <button
                className="btn btn-sm"
                style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                disabled={validating}
                onClick={() => void handleValidate()}
              >
                <ShieldCheck size={14} />
                {validating ? 'Checking…' : 'Validate workflow'}
              </button>
              {issues && <IssuesPanel issues={issues} />}

              <label style={fieldLabelStyle}>Notes (optional)</label>
              <input
                style={inputStyle}
                value={publishNotes}
                placeholder="What changed in this version"
                onChange={(e) => setPublishNotes(e.target.value)}
              />
              <button
                className="btn btn-primary btn-sm"
                style={{ width: '100%', marginTop: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                disabled={publishing}
                onClick={() => void handlePublish()}
              >
                <UploadCloud size={14} />
                {publishing ? 'Publishing…' : 'Publish version'}
              </button>
              <p style={{ ...helpTextStyle, margin: '8px 0 0' }}>
                Publishing creates an immutable version; in-flight approvals keep
                running on their pinned version. Re-publishing with no changes is a
                no-op.
              </p>
            </div>
          )}

          {/* Version history (both profiles) */}
          <div style={sectionStyle}>
            <div style={{ ...sectionTitleStyle, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Published versions</span>
              <button
                style={iconBtnStyle}
                title="Refresh versions"
                onClick={() => void loadVersions()}
              >
                <RefreshCw
                  size={13}
                  style={loadingVersions ? { animation: 'spin 0.6s linear infinite' } : undefined}
                />
              </button>
            </div>

            <div style={{ marginTop: 4 }}>
              {loadingVersions && versions.length === 0 ? (
                <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>
                  Loading versions…
                </div>
              ) : versions.length === 0 ? (
                <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>
                  Not published yet. Publish to create the first version.
                </div>
              ) : (
                <>
                  <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginBottom: 6 }}>
                    Active version:{' '}
                    <strong style={{ color: 'var(--color-text)' }}>
                      {activeVersion != null ? `v${activeVersion}` : '—'}
                    </strong>
                  </div>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          <th style={th}>Ver</th>
                          <th style={th}>Status</th>
                          <th style={th} title="Approvals still running on this version">Pending</th>
                          <th style={th}>Published</th>
                          <th style={th}>Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {versions.map((v) => (
                          <tr key={v.version}>
                            <td style={td}>
                              v{v.version}
                              {v.version === activeVersion && (
                                <span style={{ marginLeft: 4, fontSize: '0.8125rem', color: 'var(--color-success, #1a7f37)' }}>active</span>
                              )}
                            </td>
                            <td style={{ ...td, color: STATUS_COLORS[v.status] || 'var(--color-text)' }}>
                              {v.status}
                            </td>
                            <td style={td}>{v.in_flight}</td>
                            <td style={td}>
                              {v.published_at
                                ? new Date(v.published_at).toLocaleDateString()
                                : '—'}
                            </td>
                            <td style={{ ...td, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={v.notes || ''}>
                              {v.notes || '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>

            {/* Compare two versions (semantic diff) */}
            {versions.length >= 2 && (
              <div style={{ marginTop: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <GitCompare size={13} style={{ color: 'var(--color-text-secondary)' }} />
                  <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>Compare</span>
                  <VersionSelect value={diffFrom} versions={versions} onChange={setDiffFrom} />
                  <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>→</span>
                  <VersionSelect value={diffTo} versions={versions} onChange={setDiffTo} />
                  <button
                    className="btn btn-sm"
                    disabled={diffing}
                    onClick={() => void handleDiff()}
                  >
                    {diffing ? '…' : 'Compare'}
                  </button>
                  {/* Only offer migration when the chosen FROM version still has
                      approvals running on it. */}
                  {(() => {
                    const fromMeta = versions.find((v) => v.version === diffFrom)
                    if (!fromMeta || (fromMeta.in_flight || 0) <= 0) return null
                    return (
                      <button
                        className="btn btn-sm"
                        style={{ display: 'flex', alignItems: 'center', gap: 4 }}
                        disabled={migrating}
                        title={`Move the ${fromMeta.in_flight} in-flight approval(s) on v${diffFrom} onto v${diffTo}`}
                        onClick={() => void handleMigrate()}
                      >
                        <ArrowRightLeft size={12} />
                        {migrating ? 'Migrating…' : `Migrate in-flight (${fromMeta.in_flight})…`}
                      </button>
                    )
                  })()}
                </div>
                {diff && <DiffPanel diff={diff} fromV={diffFrom} toV={diffTo} />}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}

// ── Validation issues panel ─────────────────────────────────────────────────
function IssuesPanel({ issues }: { issues: ApprovalValidationIssue[] }) {
  if (issues.length === 0) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, fontSize: '0.8125rem', color: 'var(--color-success, #1a7f37)' }}>
        <CheckCircle2 size={13} /> No problems found — the workflow is executable.
      </div>
    )
  }
  return (
    <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
      {issues.map((iss, i) => {
        const err = iss.severity === 'error'
        return (
          <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: '0.8125rem', color: 'var(--color-text)' }}>
            {err
              ? <XCircle size={13} style={{ color: 'var(--color-error, #cf222e)', flexShrink: 0, marginTop: 1 }} />
              : <AlertTriangle size={13} style={{ color: '#b26a00', flexShrink: 0, marginTop: 1 }} />}
            <span>
              {iss.message}
              {iss.where ? <span style={{ color: 'var(--color-text-secondary)' }}> ({iss.where})</span> : null}
            </span>
          </div>
        )
      })}
    </div>
  )
}

// ── Version picker for the compare control ──────────────────────────────────
function VersionSelect({
  value, versions, onChange,
}: {
  value: number | null
  versions: ApprovalVersionMeta[]
  onChange: (v: number) => void
}) {
  return (
    <select
      value={value ?? ''}
      onChange={(e) => onChange(Number(e.target.value))}
      style={{
        padding: '3px 6px', fontSize: '0.8125rem', background: 'var(--color-bg)',
        border: '1px solid var(--color-border)', borderRadius: 4, color: 'var(--color-text)',
      }}
    >
      {versions.map((v) => (
        <option key={v.version} value={v.version}>v{v.version}</option>
      ))}
    </select>
  )
}

// ── Semantic diff panel (what changed between two versions) ──────────────────
function DiffPanel({ diff, fromV, toV }: { diff: ApprovalDiff; fromV: number | null; toV: number | null }) {
  const cellStyle: React.CSSProperties = {
    fontSize: '0.8125rem', color: 'var(--color-text)', padding: '2px 0',
  }
  const subtle: React.CSSProperties = { color: 'var(--color-text-secondary)' }
  const fmt = (v: unknown) =>
    v === null || v === undefined || v === '' ? '∅'
      : Array.isArray(v) ? (v.length ? v.join(', ') : '∅') : String(v)
  const nodeLabel = (x: Record<string, unknown>): string => {
    const id = x.id ?? x.name
    if (id != null && id !== '') return String(id)
    return `${String(x.from ?? '')}→${String(x.to ?? '')}`
  }

  const groups: Array<[string, ApprovalDiff['participants']]> = [
    ['Participants', diff.participants],
    ['Activities', diff.activities],
    ['Transitions', diff.transitions],
  ]
  const empty =
    !diff.binding.changed &&
    groups.every(([, g]) => !g.added.length && !g.removed.length && !g.changed.length)

  return (
    <div style={{
      marginTop: 8, border: '1px solid var(--color-border)', borderRadius: 6,
      padding: 8, background: 'var(--color-bg-secondary)',
    }}>
      <div style={{ fontSize: '0.8125rem', fontWeight: 600, marginBottom: 6, color: 'var(--color-text)' }}>
        v{fromV} → v{toV}
        {diff.checksum_changed
          ? <span style={{ marginLeft: 6, color: '#b26a00' }}>· rules differ</span>
          : <span style={{ marginLeft: 6, color: 'var(--color-success, #1a7f37)' }}>· identical rules</span>}
      </div>

      {empty && <div style={{ ...cellStyle, ...subtle, fontStyle: 'italic' }}>No executable differences.</div>}

      {diff.binding.changed && (
        <div style={{ marginBottom: 6 }}>
          <div style={{ ...cellStyle, fontWeight: 600 }}>Binding</div>
          {diff.binding.fields.map((f, i) => (
            <div key={i} style={cellStyle}>
              <span style={subtle}>{f.name}: </span>
              <span style={{ textDecoration: 'line-through', color: 'var(--color-error, #cf222e)' }}>{fmt(f.old)}</span>
              {' → '}
              <span style={{ color: 'var(--color-success, #1a7f37)' }}>{fmt(f.new)}</span>
            </div>
          ))}
        </div>
      )}

      {groups.map(([label, g]) => {
        if (!g.added.length && !g.removed.length && !g.changed.length) return null
        return (
          <div key={label} style={{ marginBottom: 6 }}>
            <div style={{ ...cellStyle, fontWeight: 600 }}>{label}</div>
            {g.added.map((a, i) => (
              <div key={`a${i}`} style={cellStyle}>
                <span style={{ color: 'var(--color-success, #1a7f37)' }}>+ added</span>{' '}
                <span style={subtle}>{nodeLabel(a)}</span>
              </div>
            ))}
            {g.removed.map((r, i) => (
              <div key={`r${i}`} style={cellStyle}>
                <span style={{ color: 'var(--color-error, #cf222e)' }}>− removed</span>{' '}
                <span style={subtle}>{nodeLabel(r)}</span>
              </div>
            ))}
            {g.changed.map((c, i) => (
              <div key={`c${i}`} style={cellStyle}>
                <span style={{ color: '#b26a00' }}>~ changed</span>{' '}
                <span style={subtle}>{c.name || c.id || `${c.from ?? ''}→${c.to ?? ''}`}</span>
                {c.fields.map((f, j) => (
                  <div key={j} style={{ ...cellStyle, paddingLeft: 14 }}>
                    <span style={subtle}>{f.name}: </span>
                    <span style={{ textDecoration: 'line-through', color: 'var(--color-error, #cf222e)' }}>{fmt(f.old)}</span>
                    {' → '}
                    <span style={{ color: 'var(--color-success, #1a7f37)' }}>{fmt(f.new)}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )
      })}
    </div>
  )
}

// ── Approver users multi-select ─────────────────────────────────────────────
// A collapsible checkbox list of project users (label = "user_name (email)")
// that stores the selected user ids into level.approver_users. When the project
// user list could not be loaded it degrades to a comma-separated free-text box
// so a level can still pin specific user ids.
function ApproverUsersPicker({
  users, selected, onChange,
}: {
  users: FunctionalRoleUser[]
  selected: string[]
  onChange: (ids: string[]) => void
}) {
  const [open, setOpen] = useState(false)

  // No user catalog → fall back to free text (keeps the level configurable).
  if (users.length === 0) {
    return (
      <>
        <input
          style={inputStyle}
          value={selected.join(', ')}
          placeholder="e.g. jdoe, asmith"
          onChange={(e) => {
            const ids = e.target.value.split(',').map((s) => s.trim()).filter(Boolean)
            onChange(ids)
          }}
        />
        <div style={{ ...fieldLabelStyle, marginTop: 4, fontStyle: 'italic' }}>
          Comma-separated user ids (optional — a level normally uses a role).
        </div>
      </>
    )
  }

  const selectedSet = new Set(selected)
  const summary = selected.length === 0
    ? 'No specific users selected'
    : users
        .filter((u) => selectedSet.has(u.user_id))
        .map((u) => u.user_name || u.user_id)
        .join(', ') || `${selected.length} selected`

  const toggle = (userId: string) => {
    const next = new Set(selectedSet)
    if (next.has(userId)) next.delete(userId)
    else next.add(userId)
    onChange(Array.from(next))
  }

  return (
    <>
      <button
        type="button"
        style={{
          ...inputStyle, textAlign: 'left', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6,
        }}
        onClick={() => setOpen((o) => !o)}
      >
        <span style={{
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          color: selected.length ? 'var(--color-text)' : 'var(--color-text-secondary)',
        }}>
          {summary}
        </span>
        {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>
      {open && (
        <div style={{
          marginTop: 4, border: '1px solid var(--color-border)', borderRadius: 4,
          background: 'var(--color-bg)', maxHeight: 180, overflowY: 'auto', padding: 4,
        }}>
          {users.map((u) => (
            <label
              key={u.user_id}
              style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: '4px 6px',
                fontSize: '0.8425rem', color: 'var(--color-text)', cursor: 'pointer',
              }}
            >
              <input
                type="checkbox"
                checked={selectedSet.has(u.user_id)}
                onChange={() => toggle(u.user_id)}
              />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {u.user_name}
                {u.email ? (
                  <span style={{ color: 'var(--color-text-secondary)' }}> ({u.email})</span>
                ) : null}
              </span>
            </label>
          ))}
        </div>
      )}
      <div style={{ ...fieldLabelStyle, marginTop: 4, fontStyle: 'italic' }}>
        Optional — a level normally uses a role. Selected users can also clear it.
      </div>
    </>
  )
}

// ── Per-action-button approval chains ──────────────────────────────────────
//
// A save and each action button are INDEPENDENT approvals: "Cancel Order" can
// need the branch head while an edit needs the line manager. Each entry here is
// a full chain (its own workflow name, profile and levels) authored with the
// SAME <LevelsSection> the CRUD chain uses. Clicking an approval-controlled
// button stages the request; the button's effect runs when the chain clears.
//
// Only server-side handlers can be deferred — a 'system' button (a client-side
// add/edit/delete) and a 'report' button have nothing to stage, so they are
// listed as not supported instead of silently ignored.
const UNAPPROVABLE_HANDLERS = new Set(['system', 'report', 'import', 'open'])

// Handlers whose result is applied to the OPEN FORM. Their business logic still
// runs (and commits) on approval, but there is no form open then, so any values
// they hand back are dropped — worth saying out loud before someone routes one.
const FORM_RESULT_HANDLERS = new Set(['item_change', 'select_grid'])

/** The engine's default workflow name for one action chain. */
function defaultActionWorkflow(objName: string, action: string): string {
  const slug = (action || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '')
  return `approval_${(objName || '').toLowerCase()}_${slug}`
}

/** Every action button on the transaction — the transaction-level buttons plus
 *  each form's own — de-duplicated by name (the engine resolves an approval
 *  binding by action NAME, so one binding covers same-named buttons). */
function collectActionButtons(model: TransactionModel): Array<{ def: ActionDef; where: string }> {
  const out: Array<{ def: ActionDef; where: string }> = []
  const seen = new Set<string>()
  const push = (raw: unknown, where: string) => {
    const def = legacyActionToDef(raw as never)
    const name = (def?.name || '').trim()
    if (!name || def.disabled) return
    const key = name.toLowerCase()
    if (seen.has(key)) return
    seen.add(key)
    out.push({ def, where })
  }
  for (const a of model.actions || []) push(a, 'Transaction')
  for (const entry of model.sql_models || []) {
    const sm = entry?.sql_model
    if (!sm) continue
    const where = (sm.form_title || `Form ${sm.form_no || ''}`).trim() || 'Form'
    for (const a of sm.actions || []) push(a, where)
  }
  return out
}

function ActionApprovalsSection({
  model, bindings, onChange, roleOptions, userOptions, project, objName,
  onOpenWorkflowDesigner,
}: {
  model: TransactionModel
  bindings: ApprovalActionBinding[]
  onChange: (bindings: ApprovalActionBinding[]) => void
  roleOptions: string[]
  userOptions: FunctionalRoleUser[]
  project: string
  objName: string
  onOpenWorkflowDesigner: (action: ApprovalActionBinding) => void
}) {
  const buttons = collectActionButtons(model)
  const [openFor, setOpenFor] = useState<Record<string, boolean>>({})
  const [busy, setBusy] = useState<string | null>(null)

  const bindingFor = (name: string) =>
    bindings.find((b) => (b.action || '').toLowerCase() === name.toLowerCase())

  const patchBinding = (name: string, patch: Partial<ApprovalActionBinding>) => {
    const idx = bindings.findIndex((b) => (b.action || '').toLowerCase() === name.toLowerCase())
    if (idx < 0) {
      onChange([...bindings, { action: name, enabled: true, profile: 'linear',
        single_active_per_record: true, levels: [], ...patch }])
      return
    }
    onChange(bindings.map((b, i) => (i === idx ? { ...b, ...patch } : b)))
  }

  const removeBinding = (name: string) =>
    onChange(bindings.filter((b) => (b.action || '').toLowerCase() !== name.toLowerCase()))

  const toggle = (name: string, on: boolean) => {
    if (on) {
      patchBinding(name, { enabled: true })
      setOpenFor((prev) => ({ ...prev, [name.toLowerCase()]: true }))
    } else {
      // Dropping the whole entry keeps the model clean — a disabled chain with
      // orphan levels is just noise the engine would ignore anyway.
      removeBinding(name)
    }
  }

  const publish = async (b: ApprovalActionBinding) => {
    if (!project || !objName) {
      toast.error('Project / object not resolved — cannot publish.')
      return
    }
    if (!(b.levels || []).some((l) => (l.name || '').trim())) {
      toast.error('Add at least one approval level before publishing.')
      return
    }
    setBusy(b.action)
    try {
      const res = await approvalsPublish(project, objName, { action: b.action })
      toast.success(res.created
        ? `Published ${res.workflow} v${res.version}`
        : `No changes — already on ${res.workflow} v${res.version}`)
    } catch (err) {
      toast.error(`Publish failed: ${parseApiError(err).message}`)
    } finally {
      setBusy(null)
    }
  }

  const validate = async (b: ApprovalActionBinding) => {
    setBusy(b.action)
    try {
      const res = await approvalsValidate(project, objName, { action: b.action })
      const blockers = (res.issues || []).filter((i) => i.severity === 'error')
      if (blockers.length === 0) toast.success('No blocking issues.')
      else toast.error(`${blockers.length} blocking issue(s): ${blockers[0].message}`)
    } catch (err) {
      toast.error(`Validate failed: ${parseApiError(err).message}`)
    } finally {
      setBusy(null)
    }
  }

  return (
    <div style={sectionStyle}>
      <div style={sectionTitleStyle}>Action buttons that need approval</div>
      <p style={{ ...helpTextStyle, marginBottom: 8 }}>
        A button can require its <strong>own</strong> approval — different
        levels and roles from the save approval above. Clicking it sends the
        request for sign-off instead of running it; the button runs only once
        that chain is fully approved (rejecting means it never runs).
      </p>

      {buttons.length === 0 && (
        <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', fontStyle: 'italic', padding: '6px 0' }}>
          This transaction has no action buttons yet. Add one on the form or the
          transaction, then come back to route it through an approval.
        </div>
      )}

      {buttons.map(({ def, where }) => {
        const name = def.name
        const key = name.toLowerCase()
        const b = bindingFor(name)
        const handler = (def.handler || '').toLowerCase()
        const unsupported = UNAPPROVABLE_HANDLERS.has(handler)
        const formResult = FORM_RESULT_HANDLERS.has(handler)
        const isOpen = !!openFor[key]
        return (
          <div
            key={key}
            style={{
              border: '1px solid var(--color-border)', borderRadius: 8,
              marginBottom: 10, background: 'var(--color-bg-secondary)', overflow: 'hidden',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 9px' }}>
              <input
                type="checkbox"
                checked={!!b}
                disabled={unsupported}
                title={unsupported
                  ? 'System and report buttons run in the browser — there is nothing to hold for approval.'
                  : 'Require approval before this button runs'}
                onChange={(e) => toggle(name, e.target.checked)}
              />
              <span style={{
                flex: 1, minWidth: 0, fontSize: '0.8425rem', fontWeight: 600,
                color: unsupported ? 'var(--color-text-secondary)' : 'var(--color-text)',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {name}
                <span style={{ fontWeight: 400, color: 'var(--color-text-secondary)' }}>
                  {' '}· {where}{unsupported ? ' · not approvable' : ''}
                </span>
              </span>
              {b && (
                <button
                  style={iconBtnStyle}
                  title={isOpen ? 'Collapse' : 'Configure this chain'}
                  onClick={() => setOpenFor((prev) => ({ ...prev, [key]: !prev[key] }))}
                >
                  {isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </button>
              )}
            </div>

            {b && isOpen && (
              <div style={{ padding: '0 9px 9px' }}>
                {formResult && (
                  <div style={{
                    display: 'flex', gap: 6, alignItems: 'flex-start',
                    margin: '0 0 8px', padding: '7px 9px', borderRadius: 6,
                    background: 'rgba(234,179,8,0.10)', fontSize: '0.8125rem',
                    color: 'var(--color-text)',
                  }}>
                    <AlertTriangle size={13} style={{ flexShrink: 0, marginTop: 1, color: '#b45309' }} />
                    <span>
                      This button hands values back to the open form. Its business
                      logic still runs on approval, but there is no form open then —
                      the returned values are dropped. Route it only if its effect
                      is what matters.
                    </span>
                  </div>
                )}
                <label style={fieldLabelStyle}>Needs approval when</label>
                <input
                  style={inputStyle}
                  value={b.condition ?? ''}
                  placeholder="e.g. amount > 100000  (blank = every click needs approval)"
                  onChange={(e) => patchBinding(name, { condition: e.target.value })}
                />
                <div style={{ ...fieldLabelStyle, marginTop: 4, fontStyle: 'italic' }}>
                  Clicks that don't match run straight away, exactly as they do today.
                </div>

                <label style={fieldLabelStyle}>Workflow name</label>
                <input
                  style={inputStyle}
                  value={b.workflow ?? ''}
                  placeholder={defaultActionWorkflow(objName, name)}
                  onChange={(e) => patchBinding(name, { workflow: e.target.value })}
                />

                <label style={{ ...checkboxRowStyle, marginTop: 10 }}>
                  <input
                    type="checkbox"
                    checked={b.single_active_per_record !== false}
                    onChange={(e) => patchBinding(name, { single_active_per_record: e.target.checked })}
                  />
                  <span>One request at a time per record</span>
                </label>

                <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                  {([
                    ['linear', 'Simple'],
                    ['advanced', 'Advanced'],
                  ] as const).map(([kind, label]) => (
                    <button
                      key={kind}
                      type="button"
                      onClick={() => patchBinding(name, { profile: kind })}
                      style={{
                        flex: 1, padding: '5px 8px', borderRadius: 6, cursor: 'pointer',
                        fontSize: '0.8125rem', color: 'var(--color-text)',
                        border: `1px solid ${(b.profile || 'linear') === kind ? 'var(--color-accent)' : 'var(--color-border)'}`,
                        background: (b.profile || 'linear') === kind
                          ? 'var(--color-accent-soft, rgba(92,107,192,0.12))' : 'var(--color-bg)',
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>

                {(b.profile || 'linear') === 'advanced' ? (
                  <div style={{ marginTop: 10 }}>
                    <p style={{ ...helpTextStyle, margin: '0 0 6px' }}>
                      This chain is drawn in the Workflow Designer (parallel paths,
                      email / SQL / call-action steps). Author and <strong>publish</strong> it there.
                    </p>
                    <button
                      className="btn btn-sm"
                      style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                      onClick={() => onOpenWorkflowDesigner(b)}
                    >
                      <ExternalLink size={13} /> Open in Workflow Designer
                    </button>
                  </div>
                ) : (
                  <LevelsSection
                    levels={b.levels || []}
                    onChange={(levels) => patchBinding(name, { levels })}
                    roleOptions={roleOptions}
                    userOptions={userOptions}
                    idPrefix={`act-${key}`}
                    title={`Levels for “${name}”`}
                    help={'Who signs this button off, top to bottom. A level with an '
                      + '"applies when" condition is skipped for records that don\'t match it.'}
                  />
                )}

                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button
                    className="btn btn-sm"
                    disabled={busy === b.action}
                    onClick={() => validate(b)}
                    style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                  >
                    <CheckCircle2 size={13} /> Validate
                  </button>
                  <button
                    className="btn btn-primary btn-sm"
                    disabled={busy === b.action}
                    onClick={() => publish(b)}
                    style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                  >
                    <UploadCloud size={13} /> Publish version
                  </button>
                </div>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

// ── Ordered approval levels ────────────────────────────────────────────────
// The level list + its mutations, owned by ONE component so every approval
// chain on this screen — the object's CRUD chain and each action button's own
// chain — is authored with exactly the same editor (identical validation,
// parallel/SLA options and ordering semantics), not a second copy that drifts.
function LevelsSection({
  levels, onChange, roleOptions, userOptions, idPrefix, title, help,
}: {
  levels: ApprovalLevel[]
  onChange: (levels: ApprovalLevel[]) => void
  roleOptions: string[]
  userOptions: FunctionalRoleUser[]
  /** Namespaces the datalist ids so several editors can coexist on a screen. */
  idPrefix: string
  title?: string
  help?: string
}) {
  const [expanded, setExpanded] = useState<Record<number, boolean>>({})

  const patchLevel = (idx: number, patch: Partial<ApprovalLevel>) =>
    onChange(levels.map((l, i) => (i === idx ? { ...l, ...patch } : l)))

  // Turning "Multiple approvers" on seeds one row from the level's existing
  // single approver_role and a default 'all' quorum; turning it off clears the
  // parallel config and keeps the single approver_role.
  const setParallel = (idx: number, on: boolean) => {
    const lvl = levels[idx]
    if (on) {
      const seed: ApprovalApprover[] = (lvl.approvers && lvl.approvers.length)
        ? lvl.approvers
        : [{ approver_role: lvl.approver_role || '' }]
      patchLevel(idx, { approvers: seed, quorum: lvl.quorum || 'all' })
    } else {
      patchLevel(idx, { approvers: undefined, quorum: undefined })
    }
  }

  const patchApprover = (
    levelIdx: number, approverIdx: number, patch: Partial<ApprovalApprover>,
  ) => {
    const cur = levels[levelIdx].approvers || []
    patchLevel(levelIdx, {
      approvers: cur.map((a, i) => (i === approverIdx ? { ...a, ...patch } : a)),
    })
  }

  const addApprover = (levelIdx: number) => {
    const cur = levels[levelIdx].approvers || []
    patchLevel(levelIdx, { approvers: [...cur, { approver_role: '' }] })
  }

  const removeApprover = (levelIdx: number, approverIdx: number) => {
    const cur = levels[levelIdx].approvers || []
    patchLevel(levelIdx, { approvers: cur.filter((_, i) => i !== approverIdx) })
  }

  const addLevel = () => {
    setExpanded((prev) => ({ ...prev, [levels.length]: true }))
    onChange([...levels, { name: `Level ${levels.length + 1}` }])
  }

  const removeLevel = (idx: number) => {
    onChange(levels.filter((_, i) => i !== idx))
    setExpanded((prev) => {
      const next: Record<number, boolean> = {}
      for (const [k, open] of Object.entries(prev)) {
        const ki = Number(k)
        if (ki === idx) continue
        next[ki > idx ? ki - 1 : ki] = open
      }
      return next
    })
  }

  const moveLevel = (idx: number, dir: -1 | 1) => {
    const to = idx + dir
    if (to < 0 || to >= levels.length) return
    const next = [...levels]
    ;[next[idx], next[to]] = [next[to], next[idx]]
    onChange(next)
    // Keep open cards following their level across the swap.
    setExpanded((prev) => ({ ...prev, [idx]: prev[to], [to]: prev[idx] }))
  }

  return (
    <div style={sectionStyle}>
      <div style={{ ...sectionTitleStyle, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>{title || 'Approval levels'} ({levels.length})</span>
        <button
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-accent)', fontSize: '0.8125rem', display: 'flex', alignItems: 'center', gap: 4 }}
          onClick={addLevel}
        >
          <Plus size={12} /> Add level
        </button>
      </div>
      <div style={{ ...fieldLabelStyle, marginTop: 0, fontStyle: 'italic' }}>
        {help || 'Levels are cleared top to bottom. A level with an "applies when" '
          + "condition is skipped for records that don't match it."}
      </div>

      {levels.length === 0 && (
        <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', fontStyle: 'italic', padding: '6px 0' }}>
          No levels yet. Click "Add level" to define who approves and when.
        </div>
      )}

      {levels.map((level, i) => {
        const isOpen = !!expanded[i]
        const nameMissing = !(level.name || '').trim()
        return (
          <div
            key={i}
            style={{
              border: '1px solid var(--color-border)', borderRadius: 8,
              marginBottom: 10, background: 'var(--color-bg-secondary)',
              overflow: 'hidden',
            }}
          >
            {/* Card header — collapsible */}
            <div
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 8px', cursor: 'pointer' }}
              onClick={() => setExpanded((prev) => ({ ...prev, [i]: !prev[i] }))}
            >
              <span style={{
                flexShrink: 0, fontSize: '0.8125rem', fontWeight: 700,
                color: 'var(--color-accent)', minWidth: 16,
              }}>{i + 1}</span>
              <span style={{
                flex: 1, fontSize: '0.8425rem', fontWeight: 600,
                color: nameMissing ? 'var(--color-text-secondary)' : 'var(--color-text)',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {level.name?.trim() || 'Untitled level'}
                {level.approver_role ? (
                  <span style={{ fontWeight: 400, color: 'var(--color-text-secondary)' }}>
                    {' '}· {level.approver_role}
                  </span>
                ) : null}
              </span>
              <button
                style={{ ...iconBtnStyle, opacity: i === 0 ? 0.3 : 1 }}
                title="Move up"
                disabled={i === 0}
                onClick={(e) => { e.stopPropagation(); moveLevel(i, -1) }}
              >
                <ChevronUp size={14} />
              </button>
              <button
                style={{ ...iconBtnStyle, opacity: i === levels.length - 1 ? 0.3 : 1 }}
                title="Move down"
                disabled={i === levels.length - 1}
                onClick={(e) => { e.stopPropagation(); moveLevel(i, 1) }}
              >
                <ChevronDown size={14} />
              </button>
              <button
                style={{ ...iconBtnStyle, color: 'var(--color-error, #cf222e)' }}
                title="Remove level"
                onClick={(e) => { e.stopPropagation(); removeLevel(i) }}
              >
                <Trash2 size={13} />
              </button>
            </div>

            {/* Card body */}
            {isOpen && (
              <div style={{ padding: '0 8px 8px' }}>
                <label style={fieldLabelStyle}>Level name</label>
                <input
                  style={inputStyle}
                  value={level.name}
                  placeholder="e.g. Manager"
                  onChange={(e) => patchLevel(i, { name: e.target.value })}
                />

                <label style={fieldLabelStyle}>Applies when</label>
                <input
                  style={inputStyle}
                  value={level.condition ?? ''}
                  placeholder="e.g. amount > 100000  (blank = always applies)"
                  onChange={(e) => patchLevel(i, { condition: e.target.value })}
                />

                <label style={fieldLabelStyle}>Approver role</label>
                <input
                  style={inputStyle}
                  value={level.approver_role ?? ''}
                  placeholder="e.g. finance"
                  list={`approver-roles-${idPrefix}-${i}`}
                  onChange={(e) => patchLevel(i, { approver_role: e.target.value })}
                />
                <datalist id={`approver-roles-${idPrefix}-${i}`}>
                  {roleOptions.map((r) => (
                    <option key={r} value={r.toUpperCase()} />
                  ))}
                </datalist>
                <div style={{ ...fieldLabelStyle, marginTop: 4, fontStyle: 'italic' }}>
                  The project role whose members approve this level. Pick an
                  existing functional role or type a new one.
                </div>

                <label style={fieldLabelStyle}>Or specific users</label>
                <ApproverUsersPicker
                  users={userOptions}
                  selected={level.approver_users || []}
                  onChange={(ids) =>
                    patchLevel(i, { approver_users: ids.length ? ids : undefined })}
                />

                <LevelAdvanced
                  level={level}
                  levelIdx={i}
                  roleOptions={roleOptions}
                  onPatch={(patch) => patchLevel(i, patch)}
                  onSetParallel={(on) => setParallel(i, on)}
                  onPatchApprover={(ai, patch) => patchApprover(i, ai, patch)}
                  onAddApprover={() => addApprover(i)}
                  onRemoveApprover={(ai) => removeApprover(i, ai)}
                />
              </div>
            )}
          </div>
        )
      })}

      <button style={addBtnStyle} onClick={addLevel}>
        <Plus size={12} /> Add level
      </button>
    </div>
  )
}

// ── Per-level "Parallel & SLA" advanced subsection ──────────────────────────
// A collapsible block inside each level card holding the Phase-3 power options
// so simple users aren't overwhelmed: a dynamic role expression, parallel
// approvers + quorum, and an SLA. All mutate the level through the parent's
// existing config-mutation path.
function LevelAdvanced({
  level, levelIdx, roleOptions, onPatch,
  onSetParallel, onPatchApprover, onAddApprover, onRemoveApprover,
}: {
  level: ApprovalLevel
  levelIdx: number
  roleOptions: string[]
  onPatch: (patch: Partial<ApprovalLevel>) => void
  onSetParallel: (on: boolean) => void
  onPatchApprover: (approverIdx: number, patch: Partial<ApprovalApprover>) => void
  onAddApprover: () => void
  onRemoveApprover: (approverIdx: number) => void
}) {
  const parallelOn = !!(level.approvers && level.approvers.length)
  // Auto-open when any advanced option is already configured so it isn't hidden.
  const [open, setOpen] = useState(
    parallelOn || !!level.role_expr || level.sla_hours != null,
  )
  const approvers = level.approvers || []
  // 'all' | 'any' | a positive integer string. The "At least N" branch shows a
  // number box; everything numeric (and not all/any) is treated as N.
  const quorum = level.quorum || 'all'
  const quorumMode: 'all' | 'any' | 'n' =
    quorum === 'all' ? 'all' : quorum === 'any' ? 'any' : 'n'

  return (
    <div style={{ marginTop: 10, borderTop: '1px dashed var(--color-border)', paddingTop: 8 }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: 0,
          display: 'flex', alignItems: 'center', gap: 6,
          fontSize: '0.8125rem', fontWeight: 600, color: 'var(--color-text-secondary)',
        }}
      >
        {open ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
        <SlidersHorizontal size={12} /> Parallel &amp; SLA
      </button>

      {open && (
        <div style={{ marginTop: 6 }}>
          {/* Dynamic role expression */}
          <label style={fieldLabelStyle}>Dynamic role (optional)</label>
          <input
            style={inputStyle}
            value={level.role_expr ?? ''}
            placeholder={'iif(amount > 100000, "senior_finance", "finance")'}
            onChange={(e) => onPatch({ role_expr: e.target.value || undefined })}
          />
          <div style={{ ...fieldLabelStyle, marginTop: 4, fontStyle: 'italic' }}>
            Dynamic role expression, e.g. iif(amount &gt; 100000, "senior_finance", "finance").
            When set, it picks the approver role at runtime.
          </div>

          {/* Parallel approvers + quorum */}
          <label style={{ ...checkboxRowStyle, marginTop: 10 }}>
            <input
              type="checkbox"
              checked={parallelOn}
              onChange={(e) => onSetParallel(e.target.checked)}
            />
            <Users size={13} style={{ color: 'var(--color-text-secondary)' }} />
            <span>Multiple approvers (parallel)</span>
          </label>

          {parallelOn && (
            <div style={{ marginTop: 6 }}>
              {approvers.map((ap, ai) => (
                <div key={ai} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                  <input
                    style={{ ...inputStyle, flex: 1 }}
                    value={ap.approver_role ?? ''}
                    placeholder="e.g. legal"
                    list={`level-${levelIdx}-approver-roles`}
                    onChange={(e) => onPatchApprover(ai, { approver_role: e.target.value })}
                  />
                  <button
                    style={{ ...iconBtnStyle, color: 'var(--color-error, #cf222e)' }}
                    title="Remove approver"
                    onClick={() => onRemoveApprover(ai)}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              ))}
              <datalist id={`level-${levelIdx}-approver-roles`}>
                {roleOptions.map((r) => (
                  <option key={r} value={r.toUpperCase()} />
                ))}
              </datalist>
              <button style={addBtnStyle} onClick={onAddApprover}>
                <Plus size={12} /> Add approver
              </button>

              <label style={fieldLabelStyle}>How many must approve (quorum)</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <select
                  value={quorumMode}
                  onChange={(e) => {
                    const v = e.target.value as 'all' | 'any' | 'n'
                    if (v === 'all') onPatch({ quorum: 'all' })
                    else if (v === 'any') onPatch({ quorum: 'any' })
                    else onPatch({ quorum: '1' })
                  }}
                  style={{ ...inputStyle, flex: 1, cursor: 'pointer' }}
                >
                  <option value="all">All of them</option>
                  <option value="any">Any one</option>
                  <option value="n">At least N…</option>
                </select>
                {quorumMode === 'n' && (
                  <input
                    type="number"
                    min={1}
                    step={1}
                    style={{ ...inputStyle, width: 80 }}
                    value={/^\d+$/.test(quorum) ? quorum : '1'}
                    onChange={(e) => {
                      const n = Math.max(1, parseInt(e.target.value, 10) || 1)
                      onPatch({ quorum: String(n) })
                    }}
                  />
                )}
              </div>
            </div>
          )}

          {/* SLA */}
          <label style={fieldLabelStyle}>
            <Clock size={11} style={{ verticalAlign: -1, marginRight: 4, color: 'var(--color-text-secondary)' }} />
            SLA (hours before overdue)
          </label>
          <input
            type="number"
            min={0}
            step={1}
            style={inputStyle}
            value={level.sla_hours ?? ''}
            placeholder="empty = no SLA"
            onChange={(e) => {
              const raw = e.target.value
              if (raw === '') { onPatch({ sla_hours: undefined }); return }
              const n = Number(raw)
              onPatch({ sla_hours: Number.isFinite(n) && n >= 0 ? n : undefined })
            }}
          />
          <div style={{ ...fieldLabelStyle, marginTop: 4, fontStyle: 'italic' }}>
            Hours allowed on this step before it is flagged overdue. Leave empty for no deadline.
          </div>
        </div>
      )}
    </div>
  )
}

// ── Tenant thresholds / overrides section ───────────────────────────────────
// Two parts: (1) DECLARATIONS edited on the model (approval.overrides[]) — the
// named knobs a level condition references as ov_<name>; (2) the CURRENT tenant
// VALUES stored per project, lazily loaded and inline-editable.
function OverridesSection({
  overrides, onPatchOverride, onAddOverride, onRemoveOverride,
  values, valuesLoaded, loadingValues, edits, setEdits, savingName,
  onLoadValues, onSaveValue, onDeleteValue,
}: {
  overrides: ApprovalOverrideDecl[]
  onPatchOverride: (idx: number, patch: Partial<ApprovalOverrideDecl>) => void
  onAddOverride: () => void
  onRemoveOverride: (idx: number) => void
  values: ApprovalOverride[]
  valuesLoaded: boolean
  loadingValues: boolean
  edits: Record<string, string>
  setEdits: React.Dispatch<React.SetStateAction<Record<string, string>>>
  savingName: string | null
  onLoadValues: () => void
  onSaveValue: (name: string, label?: string) => void
  onDeleteValue: (name: string) => void
}) {
  const [open, setOpen] = useState(false)

  return (
    <div style={sectionStyle}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          ...sectionTitleStyle, width: '100%', textAlign: 'left', cursor: 'pointer',
          background: 'none', border: 'none', borderBottom: '1px solid var(--color-border)',
          display: 'flex', alignItems: 'center', gap: 6,
        }}
      >
        {open ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
        <SlidersHorizontal size={12} /> Tenant thresholds / overrides
        {overrides.length ? (
          <span style={{ fontWeight: 400, color: 'var(--color-text-secondary)' }}>
            ({overrides.length})
          </span>
        ) : null}
      </button>

      {open && (
        <div style={{ marginTop: 8 }}>
          <p style={helpTextStyle}>
            Declare named knobs each tenant can set. A level "applies when" condition
            references one as <code>ov_&lt;name&gt;</code> — e.g. <code>amount &gt; ov_big_order</code>.
          </p>

          {/* Declarations */}
          {overrides.length === 0 && (
            <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', fontStyle: 'italic', padding: '2px 0 6px' }}>
              No overrides declared yet.
            </div>
          )}
          {overrides.map((ov, i) => (
            <div
              key={i}
              style={{
                border: '1px solid var(--color-border)', borderRadius: 6,
                padding: 8, marginBottom: 8, background: 'var(--color-bg-secondary)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>ov_</span>
                <input
                  style={{ ...inputStyle, flex: 1 }}
                  value={ov.name}
                  placeholder="big_order"
                  onChange={(e) => onPatchOverride(i, { name: e.target.value.replace(/\s+/g, '_') })}
                />
                <button
                  style={{ ...iconBtnStyle, color: 'var(--color-error, #cf222e)' }}
                  title="Remove override"
                  onClick={() => onRemoveOverride(i)}
                >
                  <Trash2 size={13} />
                </button>
              </div>
              <label style={fieldLabelStyle}>Label (optional)</label>
              <input
                style={inputStyle}
                value={ov.label ?? ''}
                placeholder="e.g. Big order threshold"
                onChange={(e) => onPatchOverride(i, { label: e.target.value || undefined })}
              />
              <label style={fieldLabelStyle}>Default value</label>
              <input
                style={inputStyle}
                value={ov.default ?? ''}
                placeholder="e.g. 100000"
                onChange={(e) => onPatchOverride(i, { default: e.target.value || undefined })}
              />
            </div>
          ))}
          <button style={addBtnStyle} onClick={onAddOverride}>
            <Plus size={12} /> Add override
          </button>

          {/* Current tenant values */}
          <div style={{ marginTop: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
              <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--color-text)' }}>
                Current tenant values
              </span>
              <button
                className="btn btn-sm"
                style={{ display: 'flex', alignItems: 'center', gap: 4 }}
                disabled={loadingValues}
                onClick={onLoadValues}
              >
                <RefreshCw
                  size={12}
                  style={loadingValues ? { animation: 'spin 0.6s linear infinite' } : undefined}
                />
                {loadingValues ? 'Loading…' : 'Load values'}
              </button>
            </div>
            <p style={{ ...helpTextStyle, margin: '6px 0' }}>
              The values stored for this project's tenant. Edit and save to override a default.
            </p>

            {!valuesLoaded ? (
              <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>
                Click "Load values" to see this tenant's stored overrides.
              </div>
            ) : values.length === 0 ? (
              <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>
                No stored override values yet — declared overrides fall back to their defaults.
              </div>
            ) : (
              values.map((v) => (
                <div
                  key={v.name}
                  style={{
                    border: '1px solid var(--color-border)', borderRadius: 6,
                    padding: 8, marginBottom: 8, background: 'var(--color-bg-secondary)',
                  }}
                >
                  <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--color-text)' }}>
                    {v.label || v.name}
                    <span style={{ fontWeight: 400, color: 'var(--color-text-secondary)' }}> · ov_{v.name}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
                    <input
                      style={{ ...inputStyle, flex: 1 }}
                      value={edits[v.name] ?? v.value}
                      onChange={(e) => setEdits((prev) => ({ ...prev, [v.name]: e.target.value }))}
                    />
                    <button
                      className="btn btn-sm"
                      style={{ display: 'flex', alignItems: 'center', gap: 4 }}
                      disabled={savingName === v.name}
                      onClick={() => onSaveValue(v.name, v.label || undefined)}
                    >
                      <Save size={12} /> Save
                    </button>
                    <button
                      style={{ ...iconBtnStyle, color: 'var(--color-error, #cf222e)' }}
                      title="Delete value"
                      disabled={savingName === v.name}
                      onClick={() => onDeleteValue(v.name)}
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                  {(v.updated_by || v.updated_at) && (
                    <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: 4 }}>
                      {v.updated_by ? `by ${v.updated_by}` : ''}
                      {v.updated_by && v.updated_at ? ' · ' : ''}
                      {v.updated_at ? new Date(v.updated_at).toLocaleString() : ''}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}
